import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { QrCode, Download, ExternalLink, Copy, CheckCircle, Coffee, TableProperties, Plus, Edit, Trash2, Eye, ToggleLeft, ToggleRight, BarChart3, Settings, Smartphone, Bot, Sparkles, TrendingUp, Package, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, Table } from '../../services/dataService';
import QrCodeLib from 'qrcode';
import { saveAs } from 'file-saver';
import { getPublicUrl } from '../../utils/domainUtils';
import AddTableModal from '../features/AddTableModal';
import EditTableModal from '../features/EditTableModal';

type FeatureTab = 'menu' | 'tables';

const Features: React.FC = () => {
  const { tenantData } = useAuth();
  const [activeTab, setActiveTab] = useState<FeatureTab>('menu');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Tables state
  const [tables, setTables] = useState<Table[]>([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [showAddTableModal, setShowAddTableModal] = useState(false);
  const [showEditTableModal, setShowEditTableModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [tableQrCodes, setTableQrCodes] = useState<Record<string, string>>({});
  const [showQrModal, setShowQrModal] = useState(false);
  const [selectedQrCode, setSelectedQrCode] = useState<string>('');
  const [selectedQrTable, setSelectedQrTable] = useState<Table | null>(null);

  // QR Ordering Control
  const [isQrOrderingEnabled, setIsQrOrderingEnabled] = useState<boolean>(true);
  const [qrStatusLoading, setQrStatusLoading] = useState(false);

  const publicMenuUrl = getPublicUrl(`menu/${tenantData?.id}`);

  const loadTables = useCallback(async () => {
    if (!tenantData?.id) return;
    
    try {
      setTablesLoading(true);
      const tablesData = await dataService.getTables(tenantData.id);
      setTables(tablesData);
    } catch (error) {
      console.error('Error loading tables:', error);
    } finally {
      setTablesLoading(false);
    }
  }, [tenantData]);

  const loadQrOrderingStatus = useCallback(async () => {
    if (!tenantData?.id) return;
    
    try {
      // Get current status from tenant data - default to true if not set
      setIsQrOrderingEnabled(tenantData.isQrOrderingEnabled !== false);
    } catch (error) {
      console.error('Error loading QR ordering status:', error);
    }
  }, [tenantData]);

  useEffect(() => {
    if (tenantData?.id) {
      generateQRCode();
      loadQrOrderingStatus();
      if (activeTab === 'tables') {
        loadTables();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantData, activeTab]);

  const generateQRCode = async () => {
    try {
      setLoading(true);
      const qrCodeData = await QrCodeLib.toDataURL(publicMenuUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#1f2937', // Dark gray
          light: '#ffffff' // White
        }
      });
      setQrCodeDataUrl(qrCodeData);
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateTableQRCode = async (table: Table) => {
    try {
      const tableMenuUrl = getPublicUrl(`menu/${tenantData?.id}/table/${table.tableNumber}`);
      const qrCodeData = await QrCodeLib.toDataURL(tableMenuUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#1f2937',
          light: '#ffffff'
        }
      });
      
      setTableQrCodes(prev => ({
        ...prev,
        [table.id!]: qrCodeData
      }));
      
      // Update table to mark QR as generated
      await dataService.updateTable(tenantData!.id, table.id!, { qrCodeGenerated: true });
      await loadTables(); // Refresh tables
      
      return qrCodeData;
    } catch (error) {
      console.error('Error generating table QR code:', error);
      return null;
    }
  };

  const downloadQRCode = () => {
    if (qrCodeDataUrl) {
      fetch(qrCodeDataUrl)
        .then(res => res.blob())
        .then(blob => {
          saveAs(blob, `${tenantData?.businessName || 'cafe'}-menu-qr.png`);
        });
    }
  };

  const downloadTableQRCode = (table: Table) => {
    const qrCode = tableQrCodes[table.id!];
    if (qrCode) {
      fetch(qrCode)
        .then(res => res.blob())
        .then(blob => {
          saveAs(blob, `${tenantData?.businessName || 'cafe'}-table-${table.tableNumber}-qr.png`);
        });
    }
  };

  const viewTableQRCode = (table: Table) => {
    const qrCode = tableQrCodes[table.id!];
    if (qrCode) {
      setSelectedQrCode(qrCode);
      setSelectedQrTable(table);
      setShowQrModal(true);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(publicMenuUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const openPublicMenu = () => {
    window.open(publicMenuUrl, '_blank');
  };

  const handleAddTable = async () => {
    await loadTables();
    setShowAddTableModal(false);
  };

  const handleEditTable = (table: Table) => {
    setSelectedTable(table);
    setShowEditTableModal(true);
  };

  const handleEditSuccess = async () => {
    await loadTables();
    setShowEditTableModal(false);
    setSelectedTable(null);
  };

  const handleDeleteTable = async (table: Table) => {
    const confirmMessage = `Are you sure you want to delete table "${table.tableNumber}"?\n\nThis action cannot be undone.`;
    
    if (window.confirm(confirmMessage)) {
      try {
        await dataService.deleteTable(tenantData!.id, table.id!);
        await loadTables();
      } catch (error) {
        console.error('Error deleting table:', error);
        alert('Failed to delete table. Please try again.');
      }
    }
  };

  const getStatusColor = (status: Table['status']) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
      case 'occupied':
        return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
      case 'reserved':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300';
      case 'maintenance':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const handleQrOrderingToggle = async () => {
    if (!tenantData?.id) return;
    
    try {
      setQrStatusLoading(true);
      const newStatus = !isQrOrderingEnabled;
      await dataService.updateQrOrderingStatus(tenantData.id, newStatus);
      setIsQrOrderingEnabled(newStatus);
    } catch (error) {
      console.error('Error updating QR ordering status:', error);
      alert('Failed to update QR ordering status. Please try again.');
    } finally {
      setQrStatusLoading(false);
    }
  };

  const tabs = [
    { id: 'menu', label: 'Menu QR', icon: Coffee },
    { id: 'tables', label: 'Table Management', icon: TableProperties }
  ];

  return (
    <div className="space-y-6 animate-page-enter">
      {/* Header */}
      <div className="text-center sm:text-left">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
          Features
        </h1>
        <p className="text-gray-600 dark:text-gray-300 text-base sm:text-lg">
          Manage QR codes and customer-facing features for your cafe
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-lg transition-all duration-300 animate-fade-in-up animation-delay-100">
        <nav className="flex space-x-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as FeatureTab)}
                className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                }`}
              >
                <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'menu' && (
        <div className="grid grid-cols-1 desktop:grid-cols-2 gap-6">
          <div className="desktop:col-span-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              Imported your menu using AI photo capture? Download and print these QR codes so customers can instantly scan and view the updated menu.
            </p>
          </div>

          {/* QR Ordering Control */}
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-lg transition-all duration-300 animate-fade-in-up animation-delay-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-r from-blue-400 to-blue-600 p-2 rounded-xl">
                  <QrCode className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">QR Ordering Control</h2>
              </div>
              <button
                onClick={handleQrOrderingToggle}
                disabled={qrStatusLoading}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  isQrOrderingEnabled
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800/40'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {qrStatusLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                ) : isQrOrderingEnabled ? (
                  <ToggleRight className="w-5 h-5" />
                ) : (
                  <ToggleLeft className="w-5 h-5" />
                )}
                <span>{isQrOrderingEnabled ? 'Enabled' : 'Disabled'}</span>
              </button>
            </div>
            
            <div className={`rounded-lg p-4 mb-4 ${
              isQrOrderingEnabled
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                : 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
            }`}>
              <p className={`text-sm font-medium ${
                isQrOrderingEnabled ? 'text-green-800 dark:text-green-300' : 'text-yellow-800 dark:text-yellow-300'
              }`}>
                {isQrOrderingEnabled
                  ? '✅ QR Ordering is ENABLED - Customers can view your menu by scanning QR codes'
                  : '⚠️ QR Ordering is DISABLED - QR codes will show a "temporarily unavailable" message'
                }
              </p>
            </div>

            <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
              <div className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-full flex items-center justify-center text-xs font-semibold">
                  ℹ
                </span>
                <p>Use this toggle to control when customers can access your menu through QR codes.</p>
              </div>
              <div className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-full flex items-center justify-center text-xs font-semibold">
                  💡
                </span>
                <p>Disable during off-hours, maintenance, or when you want to handle orders manually.</p>
              </div>
              <div className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-full flex items-center justify-center text-xs font-semibold">
                  🔄
                </span>
                <p>Changes take effect immediately - no need to regenerate QR codes.</p>
              </div>
            </div>
          </div>

          {/* AI Business Assistant - Coming Soon */}
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-lg transition-all duration-300 animate-fade-in-up animation-delay-300 border-2 border-dashed border-primary-200 dark:border-primary-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-r from-purple-400 to-purple-600 p-2 rounded-xl relative">
                  <Bot className="w-5 h-5 text-white" />
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                    AI Business Assistant
                    <span className="ml-2 px-2 py-1 bg-gradient-to-r from-green-400 to-green-600 text-white text-xs rounded-full font-medium">
                      Live
                    </span>
                  </h2>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Sparkles className="w-5 h-5 text-yellow-500 animate-bounce" />
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg p-4 mb-4 border border-yellow-200 dark:border-yellow-800">
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300 text-center">
                🚀 Get instant insights about your business with AI-powered analytics!
              </p>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-medium text-blue-800 dark:text-blue-300">Sales Analytics</span>
                  </div>
                  <p className="text-xs text-blue-600 dark:text-blue-400">Real-time sales insights and trends</p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-2">
                    <Package className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span className="text-sm font-medium text-green-800 dark:text-green-300">Inventory</span>
                  </div>
                  <p className="text-xs text-green-600 dark:text-green-400">Stock levels and alerts</p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-2">
                    <Users className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    <span className="text-sm font-medium text-purple-800 dark:text-purple-300">Customers</span>
                  </div>
                  <p className="text-xs text-purple-600 dark:text-purple-400">Customer insights and behavior</p>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-2">
                    <BarChart3 className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                    <span className="text-sm font-medium text-orange-800 dark:text-orange-300">Reports</span>
                  </div>
                  <p className="text-xs text-orange-600 dark:text-orange-400">Advanced business intelligence</p>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Sample Questions:</h4>
                <div className="space-y-1">
                  <p className="text-xs text-gray-600 dark:text-gray-300">• "What are today's sales?"</p>
                  <p className="text-xs text-gray-600 dark:text-gray-300">• "Which items are selling best?"</p>
                  <p className="text-xs text-gray-600 dark:text-gray-300">• "Show me low stock items"</p>
                  <p className="text-xs text-gray-600 dark:text-gray-300">• "Who are our top customers?"</p>
                </div>
              </div>

              <div className="text-center">
                <button 
                  onClick={() => {
                    console.log('🚀 AI Assistant button clicked - dispatching open-chatbot event');
                    window.dispatchEvent(new CustomEvent('open-chatbot'));
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg font-medium hover:from-primary-600 hover:to-primary-700 transition-all duration-200"
                >
                  <Bot className="w-4 h-4 inline mr-2" />
                  Try the AI Assistant
                </button>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Click to open the chatbot</p>
              </div>
            </div>
          </div>

          {/* QR Code Display */}
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-lg transition-all duration-300 animate-fade-in-up animation-delay-300">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-r from-purple-400 to-purple-600 p-2 rounded-xl">
                  <QrCode className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Menu QR Code</h2>
              </div>
              
              {/* Action Buttons - Now in header */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={downloadQRCode}
                  disabled={!qrCodeDataUrl}
                  className="flex items-center px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-800/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 animate-button-hover"
                  title="Download QR Code"
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">Download</span>
                </button>
                <button
                  onClick={openPublicMenu}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600/50 transition-all duration-200 animate-button-hover"
                  title="Preview Menu"
                >
                  <ExternalLink className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">Preview</span>
                </button>
              </div>
            </div>
            
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Generate a QR code that customers can scan to view your menu directly on their phones.
            </p>

            {/* QR Code Display */}
            <div className="flex justify-center">
              {loading ? (
                <div className="flex items-center justify-center w-60 h-60 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : qrCodeDataUrl ? (
                <div className="p-4 bg-white dark:bg-gray-900 rounded-lg border-2 border-gray-200 dark:border-gray-700 shadow-sm">
                  <img 
                    src={qrCodeDataUrl} 
                    alt="Menu QR Code" 
                    className="w-52 h-52"
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center w-60 h-60 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  <p className="text-gray-500 dark:text-gray-400">Unable to generate QR code</p>
                </div>
              )}
            </div>
          </div>

          {/* Instructions & Settings */}
          <div className="space-y-6">
            {/* Public Menu URL */}
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-lg transition-all duration-300 animate-fade-in-up animation-delay-400">
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-gradient-to-r from-green-400 to-green-600 p-2 rounded-xl">
                  <Smartphone className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Public Menu URL
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                Share this link directly or use the QR code for easy access.
              </p>
              
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={publicMenuUrl}
                  readOnly
                  className="input-field flex-1 bg-gray-50 dark:bg-gray-700 text-sm"
                />
                <button
                  onClick={copyToClipboard}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  title="Copy to clipboard"
                >
                  {copied ? (
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-lg transition-all duration-300 animate-fade-in-up animation-delay-500">
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-gradient-to-r from-indigo-400 to-indigo-600 p-2 rounded-xl">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  How to Use QR Code
                </h3>
              </div>
              <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                <div className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-full flex items-center justify-center text-xs font-semibold">
                    1
                  </span>
                  <p>Enable QR ordering using the toggle above.</p>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-full flex items-center justify-center text-xs font-semibold">
                    2
                  </span>
                  <p>Download the QR code image using the button above.</p>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-full flex items-center justify-center text-xs font-semibold">
                    3
                  </span>
                  <p>Print and place it on tables, at the entrance, or anywhere customers can easily scan it.</p>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-full flex items-center justify-center text-xs font-semibold">
                    4
                  </span>
                  <p>Customers scan with their phone camera to instantly view your menu.</p>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-full flex items-center justify-center text-xs font-semibold">
                    5
                  </span>
                  <p>Toggle QR ordering off during maintenance or non-service hours.</p>
                </div>
              </div>
            </div>

            {/* Cafe Info */}
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-lg transition-all duration-300 animate-fade-in-up animation-delay-600">
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-gradient-to-r from-teal-400 to-teal-600 p-2 rounded-xl">
                  <Settings className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Cafe Information
                </h3>
              </div>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Business Name:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">{tenantData?.businessName}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Contact:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">{tenantData?.email}</span>
                </div>
                {tenantData?.phone && (
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Phone:</span>
                    <span className="ml-2 text-gray-600 dark:text-gray-400">{tenantData.phone}</span>
                  </div>
                )}
                {tenantData?.address && (
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Address:</span>
                    <span className="ml-2 text-gray-600 dark:text-gray-400">{tenantData.address}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'tables' && (
        <div className="space-y-6">
          {/* Header with Add Button */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 animate-fade-in-up animation-delay-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Table Management</h2>
              <p className="text-gray-600 dark:text-gray-300">Manage your restaurant tables and generate table-specific QR codes</p>
            </div>
            <button
              onClick={() => setShowAddTableModal(true)}
              className="btn-primary animate-button-hover w-full sm:w-auto px-4 py-2 text-sm flex items-center justify-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Table
            </button>
          </div>

          {/* Tables Grid */}
          {tablesLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : tables.length === 0 ? (
            <div className="text-center py-12 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 animate-fade-in-up animation-delay-300">
              <TableProperties className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No tables found</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Get started by adding your first table to enable table-specific ordering.
              </p>
              <button
                onClick={() => setShowAddTableModal(true)}
                className="btn-primary animate-button-hover flex items-center justify-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Table
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-3 gap-4">
              {tables.map((table, index) => (
                <div key={table.id} className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-all duration-300 animate-card-hover animate-fade-in-up" style={{animationDelay: `${0.4 + index * 0.1}s`}}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">{table.tableNumber}</h3>
                      {table.name && (
                        <p className="text-sm text-gray-600 dark:text-gray-300">{table.name}</p>
                      )}
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(table.status)}`}>
                      {table.status}
                    </span>
                  </div>

                  {(table.capacity || table.location) && (
                    <div className="mb-3 space-y-1">
                      {table.capacity && (
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                          <span>Capacity: {table.capacity} people</span>
                        </div>
                      )}
                      {table.location && (
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                          Location: {table.location}
                        </div>
                      )}
                    </div>
                  )}

                  {/* QR Code Status */}
                  <div className="mb-3 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <QrCode className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {table.qrCodeGenerated ? 'QR Code Ready' : 'No QR Code'}
                        </span>
                      </div>
                      {table.qrCodeGenerated && (
                        <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                          ✓ Generated
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditTable(table)}
                        className="p-1 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                        title="Edit table"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteTable(table)}
                        className="p-1 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        title="Delete table"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="flex space-x-2">
                      {table.qrCodeGenerated && tableQrCodes[table.id!] ? (
                        <>
                          <button
                            onClick={() => viewTableQRCode(table)}
                            className="btn-secondary text-xs px-2 py-1 animate-button-hover flex items-center"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View QR
                          </button>
                          <button
                            onClick={() => downloadTableQRCode(table)}
                            className="btn-primary text-xs px-2 py-1 animate-button-hover flex items-center"
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Download
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => generateTableQRCode(table)}
                          className="btn-primary text-xs px-2 py-1 animate-button-hover flex items-center"
                        >
                          <QrCode className="w-3 h-3 mr-1" />
                          Generate QR
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showAddTableModal && (
        <AddTableModal
          onClose={() => setShowAddTableModal(false)}
          onSuccess={handleAddTable}
        />
      )}

      {showEditTableModal && selectedTable && (
        <EditTableModal
          table={selectedTable}
          onClose={() => setShowEditTableModal(false)}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* QR Code View Modal */}
      {showQrModal && selectedQrCode && selectedQrTable && createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" style={{ zIndex: 999999 }}>
          <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-2xl p-6 max-w-md w-full shadow-2xl animate-fade-in-up">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-r from-purple-400 to-purple-600 p-2 rounded-xl">
                  <QrCode className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Table {selectedQrTable.tableNumber} QR Code
                </h3>
              </div>
              <button
                onClick={() => setShowQrModal(false)}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="text-center mb-6">
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-white dark:bg-gray-900 rounded-lg border-2 border-gray-200 dark:border-gray-700 shadow-sm">
                  <img 
                    src={selectedQrCode} 
                    alt={`Table ${selectedQrTable.tableNumber} QR Code`} 
                    className="w-48 h-48"
                  />
                </div>
              </div>
              
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <p className="font-medium text-gray-900 dark:text-white">
                  Table {selectedQrTable.tableNumber}
                </p>
                {selectedQrTable.name && (
                  <p className="text-gray-600 dark:text-gray-400">{selectedQrTable.name}</p>
                )}
                <p className="text-xs">
                  Scan this QR code to access the menu for this specific table
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => downloadTableQRCode(selectedQrTable)}
                className="btn-primary animate-button-hover flex items-center justify-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </button>
              <button
                onClick={() => setShowQrModal(false)}
                className="btn-secondary animate-button-hover flex items-center justify-center"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Features; 